import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from './SEO';
import { ToolPageExtras } from '../seo/ToolPageExtras';
import { useAdRedirect, AdToast } from '../hooks/useAdRedirect';
import { AdUnit } from './AdUnit';
import { 
  Pipette, 
  Upload, 
  Download, 
  RefreshCw, 
  ArrowLeft, 
  Copy, 
  Check, 
  Sparkles, 
  Info,
  Trash2,
  FileText
} from 'lucide-react';

export const ImageColorPicker = () => {
  const { withAd, toast } = useAdRedirect();
  const [file, setFile] = useState(null);
  const [imageSrc, setImageSrc] = useState('');
  const [hoverColor, setHoverColor] = useState({ hex: '#0575fb', rgb: 'rgb(5, 117, 251)', hsl: 'hsl(213, 97%, 50%)' });
  const [selectedColor, setSelectedColor] = useState({ hex: '#0575fb', rgb: 'rgb(5, 117, 251)', hsl: 'hsl(213, 97%, 50%)' });
  const [palette, setPalette] = useState(['#0575fb', '#0ea5e9', '#6366f1', '#a855f7', '#ec4899']);
  const [copiedText, setCopiedText] = useState('');
  const [magnifier, setMagnifier] = useState({ x: 0, y: 0, show: false, color: '#0575fb' });

  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);

  // RGB to HSL helper
  const rgbToHsl = (r, g, b) => {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
        default: break;
      }
      h /= 6;
    }
    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  // Convert individual rgb values to full hex
  const rgbToHex = (r, g, b) => {
    const toHex = (c) => {
      const hex = c.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };
    return '#' + toHex(r) + toHex(g) + toHex(b);
  };

  // Handle Drag & Drop
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const processFile = (fileObj) => {
    if (!fileObj.type.startsWith('image/')) {
      alert('Please upload a valid image file (PNG, JPG, WEBP, etc.).');
      return;
    }
    setFile(fileObj);
    const reader = new FileReader();
    reader.onload = (e) => {
      setImageSrc(e.target.result);
    };
    reader.readAsDataURL(fileObj);
  };

  // Draw image to Canvas when loaded
  useEffect(() => {
    if (!imageSrc) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      
      // Calculate responsive dimensions keeping aspect ratio
      const maxDisplayWidth = Math.min(800, window.innerWidth - 64);
      let displayWidth = img.width;
      let displayHeight = img.height;

      if (img.width > maxDisplayWidth) {
        displayWidth = maxDisplayWidth;
        displayHeight = (img.height / img.width) * maxDisplayWidth;
      }

      canvas.width = displayWidth;
      canvas.height = displayHeight;
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
    };
    img.src = imageSrc;
  }, [imageSrc]);

  // Handle canvas mouse move (hover tracking)
  const handleCanvasMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    try {
      const pixel = ctx.getImageData(x, y, 1, 1).data;
      const r = pixel[0];
      const g = pixel[1];
      const b = pixel[2];
      const hex = rgbToHex(r, g, b);
      const hsl = rgbToHsl(r, g, b);

      const colorData = {
        hex,
        rgb: `rgb(${r}, ${g}, ${b})`,
        hsl: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)`
      };

      setHoverColor(colorData);
      setMagnifier({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        show: true,
        color: hex
      });
    } catch (err) {
      console.warn("Could not get color data from pixel", err);
    }
  };

  // Handle canvas click (color pick)
  const handleCanvasClick = () => {
    setSelectedColor(hoverColor);
    // Add to palette if not already exists
    if (!palette.includes(hoverColor.hex)) {
      setPalette([hoverColor.hex, ...palette.slice(0, 9)]); // limit to 10
    }
  };

  const handleCanvasMouseLeave = () => {
    setMagnifier(prev => ({ ...prev, show: false }));
  };

  // Copy values helpers
  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedText(text);
    setTimeout(() => setCopiedText(''), 2000);
  };

  const handleReset = () => {
    setFile(null);
    setImageSrc('');
    setHoverColor({ hex: '#0575fb', rgb: 'rgb(5, 117, 251)', hsl: 'hsl(213, 97%, 50%)' });
    setSelectedColor({ hex: '#0575fb', rgb: 'rgb(5, 117, 251)', hsl: 'hsl(213, 97%, 50%)' });
    setMagnifier({ x: 0, y: 0, show: false, color: '#0575fb' });
  };

  // Export picked palette (Gated behind useAdRedirect)
  const handleExportPalette = () => {
    const content = `Toolmetrico Color Palette Export\nCreated: ${new Date().toLocaleDateString()}\n\n` + 
      palette.map((color, index) => `Color ${index + 1}: ${color}`).join('\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `toolmetrico-palette-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Theme Styles
  const cardStyle = {
    background: '#ffffff',
    border: '1px solid var(--border-color)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: 'var(--shadow-sm)',
    transition: 'var(--transition-smooth)'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '0.78rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-muted)',
    fontWeight: 800,
    marginBottom: '8px'
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%', paddingBottom: '60px' }}>
      <SEO 
        title="Free Online Image Color Picker | Get HEX, RGB, HSL Palette"
        description="Upload an image and extract colors instantly with our free online image color picker. Get HEX, RGB, HSL values, utilize our live zoom magnifier, and build custom color palettes."
        keywords="image color picker, color picker from image, online color identifier, get color from image, hex code finder, rgb color picker, hsl code generator, free color palette maker"
      />

      {/* Breadcrumbs Navigation */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px', fontSize: '0.9rem' }}>
        <Link to="/" style={{ color: 'var(--text-secondary)', textDecoration: 'none', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <ArrowLeft size={16} /> Home
        </Link>
        <span style={{ color: 'var(--text-muted)' }}>/</span>
        <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>Image Color Picker</span>
      </div>

      <div className="tool-header-grid" style={{ marginBottom: '40px', textAlign: 'center' }}>
        <div style={{ display: 'inline-flex', background: 'rgba(5, 117, 251, 0.06)', color: 'var(--primary)', padding: '10px 18px', borderRadius: '30px', fontSize: '0.8rem', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: '14px', alignItems: 'center', gap: '6px' }}>
          <Pipette size={14} /> 100% Client-Side Extraction
        </div>
        <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 900, color: 'var(--text-primary)', letterSpacing: '-0.025em', margin: '0 0 12px 0', lineHeight: 1.1 }}>
          Online <span className="text-gradient" style={{ background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Image Color Picker</span>
        </h1>
        <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', maxWidth: '680px', margin: '0 auto', lineHeight: 1.55 }}>
          Upload any picture to instantly pick colors from individual pixels. Generate precise HEX, RGB, HSL color codes with magnifier lens precision.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: imageSrc ? '1fr 380px' : '1fr', gap: '32px' }} className="image-compressor-grid">
        
        {/* Left Column: Image Canvas & Upload Area */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {!imageSrc ? (
            <div 
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current.click()}
              style={{
                ...cardStyle,
                border: '2px dashed var(--primary)',
                background: 'rgba(5, 117, 251, 0.02)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 40px',
                cursor: 'pointer',
                textAlign: 'center',
                gap: '16px'
              }}
              className="dropzone"
            >
              <div style={{ background: 'rgba(5, 117, 251, 0.08)', color: 'var(--primary)', padding: '20px', borderRadius: '50%' }}>
                <Upload size={32} />
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '1.2rem', fontWeight: 800 }}>Drag & Drop Image Here</h3>
                <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Supports PNG, JPG, WEBP, SVG, and GIF up to 50MB</p>
              </div>
              <button style={{ background: 'var(--primary)', color: '#ffffff', border: 'none', borderRadius: '10px', padding: '10px 24px', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                Browse Files
              </button>
              <input 
                type="file" 
                ref={fileInputRef} 
                onChange={handleFileChange} 
                style={{ display: 'none' }} 
                accept="image/*" 
              />
            </div>
          ) : (
            <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', overflow: 'hidden' }}>
              <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-secondary)' }}>
                  Image Name: <strong style={{ color: 'var(--text-primary)' }}>{file?.name}</strong>
                </span>
                <button onClick={handleReset} style={{ background: 'rgba(239, 68, 68, 0.08)', color: '#ef4444', border: 'none', borderRadius: '8px', padding: '6px 14px', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <RefreshCw size={12} /> Reset Canvas
                </button>
              </div>

              <div style={{ position: 'relative', cursor: 'crosshair', overflow: 'hidden', borderRadius: '12px', border: '1px solid var(--border-color)', maxWidth: '100%' }}>
                <canvas 
                  ref={canvasRef}
                  onMouseMove={handleCanvasMouseMove}
                  onClick={handleCanvasClick}
                  onMouseLeave={handleCanvasMouseLeave}
                  style={{ display: 'block', maxWidth: '100%' }}
                />
                
                {/* Magnifier Lens */}
                {magnifier.show && (
                  <div style={{
                    position: 'absolute',
                    left: `${magnifier.x + 15}px`,
                    top: `${magnifier.y + 15}px`,
                    width: '80px',
                    height: '80px',
                    borderRadius: '50%',
                    border: '3px solid #ffffff',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                    backgroundColor: magnifier.color,
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <div style={{ width: '8px', height: '8px', border: '1px solid #ffffff', borderRadius: '50%' }} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Color Swatches & Values & Saved Palette */}
        {imageSrc && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Color Swatch Panel */}
            <div style={cardStyle}>
              <span style={labelStyle}>Selected Color Swatch</span>
              <div style={{ width: '100%', height: '110px', borderRadius: '14px', backgroundColor: selectedColor.hex, transition: 'background-color 0.2s', border: '1px solid var(--border-color)', marginBottom: '16px', display: 'flex', alignItems: 'flex-end', padding: '16px', boxSizing: 'border-box' }}>
                <span style={{ background: '#ffffff', color: 'var(--text-primary)', padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 800, boxShadow: 'var(--shadow-sm)' }}>
                  {selectedColor.hex}
                </span>
              </div>

              {/* Color Code Copy Blocks */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                
                {/* HEX */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)' }}>HEX Code</span>
                    <button onClick={() => handleCopy(selectedColor.hex)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {copiedText === selectedColor.hex ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                      {copiedText === selectedColor.hex ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <input type="text" readOnly value={selectedColor.hex} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--bg-secondary)', outline: 'none' }} />
                </div>

                {/* RGB */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)' }}>RGB Code</span>
                    <button onClick={() => handleCopy(selectedColor.rgb)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {copiedText === selectedColor.rgb ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                      {copiedText === selectedColor.rgb ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <input type="text" readOnly value={selectedColor.rgb} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--bg-secondary)', outline: 'none' }} />
                </div>

                {/* HSL */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)' }}>HSL Code</span>
                    <button onClick={() => handleCopy(selectedColor.hsl)} style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {copiedText === selectedColor.hsl ? <Check size={12} style={{ color: '#059669' }} /> : <Copy size={12} />}
                      {copiedText === selectedColor.hsl ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                  <input type="text" readOnly value={selectedColor.hsl} style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid var(--border-color)', fontSize: '0.85rem', fontWeight: 700, color: 'var(--text-primary)', background: 'var(--bg-secondary)', outline: 'none' }} />
                </div>

              </div>
            </div>

            {/* Saved Palette Panel */}
            <div style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ ...labelStyle, marginBottom: 0 }}>Custom Palette</span>
                <button onClick={() => setPalette([])} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Trash2 size={12} /> Clear
                </button>
              </div>

              {palette.length > 0 ? (
                <div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                    {palette.map((color, index) => (
                      <div 
                        key={index}
                        onClick={() => setSelectedColor({ hex: color, rgb: '', hsl: '' })} // minimal reload for clicking
                        title="Click to select this color option"
                        style={{
                          width: '38px',
                          height: '38px',
                          borderRadius: '8px',
                          backgroundColor: color,
                          cursor: 'pointer',
                          border: selectedColor.hex === color ? '3px solid var(--primary)' : '1px solid var(--border-color)',
                          boxShadow: 'var(--shadow-sm)',
                          transition: 'transform 0.15s'
                        }}
                        className="palette-color-box"
                      />
                    ))}
                  </div>

                  <AdToast message={toast} />
                  <button
                    onClick={withAd('export-palette', handleExportPalette)}
                    style={{
                      width: '100%',
                      background: 'rgba(5, 117, 251, 0.08)',
                      color: 'var(--primary)',
                      border: 'none',
                      borderRadius: '10px',
                      padding: '12px',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      transition: 'var(--transition-smooth)'
                    }}
                  >
                    <Download size={14} /> Download Palette (.txt)
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', color: 'var(--text-muted)', textAlign: 'center', gap: '8px' }}>
                  <Info size={16} />
                  <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>Palette Empty</span>
                  <p style={{ margin: 0, fontSize: '0.72rem' }}>Hover & click the image to construct customized color sets.</p>
                </div>
              )}
            </div>

          </div>
        )}
      </div>

      <AdUnit />
      <ToolPageExtras toolId="image-color-picker" />
    </div>
  );
};

export default ImageColorPicker;
